import React from 'react';
import { Link } from 'react-router-dom';
import './Button.css';

/**
 * Componente Button reutilizable
 * 
 * @param {string} variant - Variante del botón ('primary' | 'secondary' | 'card') - default 'primary'
 * @param {string} to - Ruta interna (usa React Router Link)
 * @param {string} href - URL externa (usa <a> con target="_blank")
 * @param {function} onClick - Handler para eventos click (usa <button>)
 * @param {string} type - Tipo del botón ('button' | 'submit' | 'reset') - default 'button'
 * @param {boolean} disabled - Estado deshabilitado
 * @param {string} ariaLabel - Etiqueta de accesibilidad
 * @param {ReactNode} icon - Icono (FontAwesome u otro)
 * @param {string} iconPosition - Posición del icono ('left' | 'right') - default 'left'
 * @param {string} className - Clases CSS adicionales
 * @param {ReactNode} children - Contenido del botón
 */
const Button = ({
    variant = 'primary',
    to,
    href,
    onClick,
    type = 'button',
    disabled = false,
    ariaLabel,
    icon,
    iconPosition = 'left',
    className = '',
    children,
    ...props
}) => {
    // Mapeo de variantes a clases CSS
    const variantClasses = {
        primary: 'btn primario',
        secondary: 'btn secundario',
        card: 'btn-card'
    };

    const baseClass = variantClasses[variant] || variantClasses.primary;
    const finalClass = `${baseClass} ${className} ${disabled ? 'disabled' : ''}`.trim();

    // Contenido del botón con icono
    const content = (
        <>
            {icon && iconPosition === 'left' && <span className="btn-icon btn-icon-left">{icon}</span>}
            {children && <span className="btn-text">{children}</span>}
            {icon && iconPosition === 'right' && <span className="btn-icon btn-icon-right">{icon}</span>}
        </>
    );

    // Si tiene 'to', es un Link interno
    if (to && !disabled) {
        return (
            <Link
                to={to}
                className={finalClass}
                aria-label={ariaLabel}
                {...props}
            >
                {content}
            </Link>
        );
    }

    // Si tiene 'href', es un enlace externo
    if (href && !disabled) {
        return (
            <a
                href={href}
                className={finalClass}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel}
                {...props}
            >
                {content}
            </a>
        );
    }

    // Por defecto, es un <button>
    return (
        <button
            type={type}
            className={finalClass}
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            {...props}
        >
            {content}
        </button>
    );
};

export default React.memo(Button);
